import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Car, Users, DollarSign, Wrench, RefreshCw,
  Calendar, CheckCircle, ArrowLeft, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { relatoriosService, AlertasData, ResumoDaLista, SlugAlerta } from '../../services/relatorios';
import { formatDate, formatCurrency } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { mensagemDoErro } from '../../utils/erros';
import { Paginacao } from '../../types';

/** Tamanho de pagina do modo detalhamento (uma lista de alerta por vez). */
const ITENS_POR_PAGINA = 15;

type FiltroCategoria = 'todos' | 'veiculos' | 'motoristas' | 'manutencoes' | 'despesas';
type TipoAlerta = 'danger' | 'warning' | 'info';

/** Alerta ja pronto para a tela, independente de ter vindo do painel ou da rota paginada. */
interface AlertaExibivel {
  id: string;
  tipo: TipoAlerta;
  categoria: FiltroCategoria;
  titulo: string;
  descricao: string;
  detalhe: string;
  icon: React.ComponentType<{ className?: string }>;
  /**
   * Pagina do registro que originou o alerta.
   *
   * Nulo quando o item nao tem id — a manutencao agrupada e um caso real —, e
   * ai o cartao nao vira clicavel: um clique que nao leva a lugar nenhum e pior
   * que nao ter clique.
   */
  destino: string | null;
}

/** Regra de apresentacao de uma lista de alerta: mesma fonte para o painel e para o detalhamento. */
interface RegraDeExibicao {
  prefixoId: string;
  tipo: TipoAlerta;
  categoria: FiltroCategoria;
  titulo: string;
  icon: React.ComponentType<{ className?: string }>;
  descricao: (item: any) => string;
  detalhe: (item: any) => string;
  /** Caminho do array correspondente dentro da resposta do painel (`getAlertas`). */
  itensDoPainel: (dados: AlertasData) => any[];
}

const descricaoDoVeiculo = (v: any) => `${v.marca} ${v.modelo} - ${v.placa}`;
const descricaoDoMotorista = (m: any) => `${m.nome} - CNH ${m.cnh}`;
const descricaoDaDespesa = (d: any) => `${d.descricao} - ${formatCurrency(d.valor)}`;

/**
 * Tabela unica de apresentacao por lista. Centralizar aqui garante que um item
 * renderizado no painel e o mesmo item renderizado na lista paginada apareçam
 * com o mesmo icone, cor, titulo e texto.
 *
 * A ordem das chaves tambem define a ordem de exibicao no painel e na faixa de
 * truncamento.
 */
const REGRAS_DE_EXIBICAO: Record<SlugAlerta, RegraDeExibicao> = {
  // A ordem das chaves define a ordem de exibicao: os vencidos vem primeiro,
  // porque sao os que exigem acao hoje. Licenciamento vencido e infracao grave
  // com apreensao do veiculo; seguro vencido deixa o patrimonio descoberto.
  'licenciamento-vencido': {
    prefixoId: 'lic-v',
    tipo: 'danger',
    categoria: 'veiculos',
    titulo: 'Licenciamento Vencido',
    icon: Car,
    descricao: descricaoDoVeiculo,
    detalhe: (v) => `Venceu em ${formatDate(v.vencimento_licenciamento)}`,
    itensDoPainel: (d) => d.veiculos?.licenciamento_vencido || [],
  },
  'seguro-vencido': {
    prefixoId: 'seg-v',
    tipo: 'danger',
    categoria: 'veiculos',
    titulo: 'Seguro Vencido',
    icon: Car,
    descricao: descricaoDoVeiculo,
    detalhe: (v) => `Venceu em ${formatDate(v.vencimento_seguro)}`,
    itensDoPainel: (d) => d.veiculos?.seguro_vencido || [],
  },
  'cnh-vencida': {
    prefixoId: 'cnh-v',
    tipo: 'danger',
    categoria: 'motoristas',
    titulo: 'CNH Vencida',
    icon: Users,
    descricao: descricaoDoMotorista,
    detalhe: (m) => `Venceu em ${formatDate(m.vencimento_cnh)}`,
    itensDoPainel: (d) => d.motoristas?.cnh_vencida || [],
  },
  'cnh-vencendo': {
    prefixoId: 'cnh-p',
    tipo: 'warning',
    categoria: 'motoristas',
    titulo: 'CNH Vencendo',
    icon: Users,
    descricao: descricaoDoMotorista,
    detalhe: (m) => `Vence em ${formatDate(m.vencimento_cnh)}`,
    itensDoPainel: (d) => d.motoristas?.cnh_vencendo || [],
  },
  'licenciamento-vencendo': {
    prefixoId: 'lic',
    tipo: 'warning',
    categoria: 'veiculos',
    titulo: 'Licenciamento Vencendo',
    icon: Car,
    descricao: descricaoDoVeiculo,
    detalhe: (v) => `Vence em ${formatDate(v.vencimento_licenciamento)}`,
    itensDoPainel: (d) => d.veiculos?.licenciamento_vencendo || [],
  },
  'seguro-vencendo': {
    prefixoId: 'seg',
    tipo: 'warning',
    categoria: 'veiculos',
    titulo: 'Seguro Vencendo',
    icon: Car,
    descricao: descricaoDoVeiculo,
    detalhe: (v) => `Vence em ${formatDate(v.vencimento_seguro)}`,
    itensDoPainel: (d) => d.veiculos?.seguro_vencendo || [],
  },
  'manutencoes-preventivas': {
    prefixoId: 'man',
    tipo: 'info',
    categoria: 'manutencoes',
    titulo: 'Manutenção agendada',
    icon: Wrench,
    descricao: (m) => m.descricao,
    detalhe: (m) => `Agendada para ${formatDate(m.data_manutencao)}`,
    itensDoPainel: (d) => d.manutencoes?.preventivas_pendentes || [],
  },
  'despesas-vencidas': {
    prefixoId: 'desp-v',
    tipo: 'danger',
    categoria: 'despesas',
    titulo: 'Despesa Vencida',
    icon: DollarSign,
    descricao: descricaoDaDespesa,
    detalhe: (d) => `Venceu em ${formatDate(d.data_vencimento)}`,
    itensDoPainel: (d) => d.despesas?.vencidas || [],
  },
  'despesas-vencendo': {
    prefixoId: 'desp-p',
    tipo: 'warning',
    categoria: 'despesas',
    titulo: 'Despesa Vencendo',
    icon: DollarSign,
    descricao: descricaoDaDespesa,
    detalhe: (d) => `Vence em ${formatDate(d.data_vencimento)}`,
    itensDoPainel: (d) => d.despesas?.vencendo || [],
  },
};

const SLUGS: SlugAlerta[] = Object.keys(REGRAS_DE_EXIBICAO) as SlugAlerta[];

/** Quais listas cada filtro de categoria abrange. */
const SLUGS_POR_FILTRO: Record<FiltroCategoria, SlugAlerta[]> = {
  todos: SLUGS,
  veiculos: ['licenciamento-vencido', 'seguro-vencido', 'licenciamento-vencendo', 'seguro-vencendo'],
  motoristas: ['cnh-vencendo', 'cnh-vencida'],
  manutencoes: ['manutencoes-preventivas'],
  despesas: ['despesas-vencendo', 'despesas-vencidas'],
};

/**
 * Rotulo de reserva, espelhando `RelatorioController::ROTULOS_DAS_LISTAS`. O
 * resumo da API traz o rotulo oficial; isto so cobre payload sem `resumo`.
 */
const ROTULO_PADRAO: Record<SlugAlerta, string> = {
  'cnh-vencida': 'CNH vencida',
  'cnh-vencendo': 'CNH vencendo',
  'licenciamento-vencendo': 'Licenciamento vencendo',
  'licenciamento-vencido': 'Licenciamento vencido',
  'seguro-vencendo': 'Seguro vencendo',
  'seguro-vencido': 'Seguro vencido',
  'manutencoes-preventivas': 'Manutenção preventiva',
  'despesas-vencidas': 'Despesa vencida',
  'despesas-vencendo': 'Despesa vencendo',
};

/** Converte um item bruto da API no formato exibido, conforme a lista de origem. */
/**
 * Tela de cadastro de cada categoria de alerta.
 *
 * `todos` nao entra: e um filtro da tela, e nao a origem de um alerta.
 */
const PAGINA_DO_REGISTRO: Partial<Record<FiltroCategoria, string>> = {
  veiculos: '/veiculos',
  motoristas: '/motoristas',
  manutencoes: '/manutencoes',
  despesas: '/despesas',
};

const paraAlertaExibivel = (item: any, slug: SlugAlerta, indice: number): AlertaExibivel => {
  const regra = REGRAS_DE_EXIBICAO[slug];
  const pagina = PAGINA_DO_REGISTRO[regra.categoria];

  return {
    // `indice` entra na chave porque o detalhamento pode receber itens sem `id`
    // (ex.: manutencao agrupada) e a lista nao pode colidir chaves de React.
    id: `${regra.prefixoId}-${item?.id ?? indice}`,
    tipo: regra.tipo,
    categoria: regra.categoria,
    titulo: regra.titulo,
    descricao: regra.descricao(item),
    detalhe: regra.detalhe(item),
    icon: regra.icon,
    // Sem id nao ha registro para abrir; ver a nota em AlertaExibivel.
    destino: pagina && item?.id ? `${pagina}/${item.id}` : null,
  };
};

const CARDS_CATEGORIA: Array<{
  key: FiltroCategoria;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}> = [
  { key: 'todos', label: 'Todos', icon: AlertTriangle, color: 'bg-gray-100 text-gray-700' },
  { key: 'veiculos', label: 'Veículos', icon: Car, color: 'bg-blue-100 text-blue-700' },
  { key: 'motoristas', label: 'Motoristas', icon: Users, color: 'bg-green-100 text-green-700' },
  { key: 'manutencoes', label: 'Manutenções', icon: Wrench, color: 'bg-purple-100 text-purple-700' },
  { key: 'despesas', label: 'Despesas', icon: DollarSign, color: 'bg-orange-100 text-orange-700' },
];

const tipoClasses: Record<TipoAlerta, string> = {
  danger: 'bg-red-50 border-red-200',
  warning: 'bg-yellow-50 border-yellow-200',
  info: 'bg-blue-50 border-blue-200',
};

const tipoIconClasses: Record<TipoAlerta, string> = {
  danger: 'text-red-500',
  warning: 'text-yellow-500',
  info: 'text-blue-500',
};

const tipoBadgeClasses: Record<TipoAlerta, string> = {
  danger: 'bg-red-100 text-red-700',
  warning: 'bg-yellow-100 text-yellow-700',
  info: 'bg-blue-100 text-blue-700',
};

const tipoBadgeTexto: Record<TipoAlerta, string> = {
  danger: 'Urgente',
  warning: 'Atencao',
  info: 'Info',
};

const CartaoAlerta: React.FC<{ alerta: AlertaExibivel }> = ({ alerta }) => {
  const navigate = useNavigate();
  const Icon = alerta.icon;
  // <button> e nao <div onClick>: assim o cartao entra na navegacao por teclado
  // e o leitor de tela anuncia que da para abrir. `text-left` desfaz a
  // centralizacao que o botao traz por padrao.
  const Envolucro = alerta.destino ? 'button' : 'div';
  const propsDeClique = alerta.destino
    ? {
        type: 'button' as const,
        onClick: () => navigate(alerta.destino!),
        title: 'Abrir o cadastro',
        className: `w-full text-left p-4 rounded-xl border transition-shadow hover:shadow-md
          focus:outline-none focus:ring-2 focus:ring-primary-500 ${tipoClasses[alerta.tipo]}`,
      }
    : { className: `p-4 rounded-xl border ${tipoClasses[alerta.tipo]}` };

  return (
    <Envolucro {...propsDeClique}>
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 ${tipoIconClasses[alerta.tipo]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-semibold text-gray-900">{alerta.titulo}</p>
            <span className={`px-2 py-0.5 text-xs rounded-full ${tipoBadgeClasses[alerta.tipo]}`}>
              {tipoBadgeTexto[alerta.tipo]}
            </span>
          </div>
          <p className="text-sm text-gray-700 mt-0.5">{alerta.descricao}</p>
          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
            <Calendar className="h-3 w-3" /> {alerta.detalhe}
          </p>
        </div>
        {alerta.destino && <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />}
      </div>
    </Envolucro>
  );
};

const AlertasList: React.FC = () => {
  const [alertas, setAlertas] = useState<AlertasData | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<FiltroCategoria>('todos');

  // Modo detalhamento: uma unica lista, paginada pelo backend.
  const [listaDetalhada, setListaDetalhada] = useState<SlugAlerta | null>(null);
  const [paginaDetalhe, setPaginaDetalhe] = useState(1);
  const [itensDetalhe, setItensDetalhe] = useState<any[]>([]);
  const [paginacaoDetalhe, setPaginacaoDetalhe] = useState<Paginacao | null>(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await relatoriosService.getAlertas();
      if (res.success) setAlertas(res.data);
    } catch (err: any) {
      toast.error(mensagemDoErro(err, 'Erro ao carregar alertas'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!listaDetalhada) return;

    // Descarta a resposta se o usuario trocar de pagina/lista antes dela chegar.
    let cancelado = false;

    const carregarPagina = async () => {
      setCarregandoDetalhe(true);
      try {
        const res = await relatoriosService.getAlertasLista(listaDetalhada, paginaDetalhe, ITENS_POR_PAGINA);
        if (cancelado) return;
        if (res.success) {
          setItensDetalhe(res.data || []);
          setPaginacaoDetalhe(res.pagination);
        }
      } catch (err: any) {
        if (!cancelado) toast.error(mensagemDoErro(err, 'Erro ao carregar a lista de alertas'));
      } finally {
        if (!cancelado) setCarregandoDetalhe(false);
      }
    };

    carregarPagina();
    return () => { cancelado = true; };
  }, [listaDetalhada, paginaDetalhe]);

  const resumo = alertas?.resumo;

  const allAlerts: AlertaExibivel[] = alertas
    ? SLUGS.flatMap((slug) =>
        REGRAS_DE_EXIBICAO[slug].itensDoPainel(alertas).map((item, i) => paraAlertaExibivel(item, slug, i))
      )
    : [];

  const alertasFiltrados = filtro === 'todos' ? allAlerts : allAlerts.filter((a) => a.categoria === filtro);

  /**
   * Contagem dos cards: o resumo conhece o total real de cada lista, enquanto o
   * array carregado traz no maximo `limite_por_lista` itens. Sem resumo (backend
   * anterior a esse campo) resta contar o que veio.
   */
  const totalDaLista = (slug: SlugAlerta) => resumo?.listas?.[slug]?.total ?? 0;
  const contagemPorCategoria = (categoria: FiltroCategoria) =>
    resumo
      ? SLUGS_POR_FILTRO[categoria].reduce((soma, slug) => soma + totalDaLista(slug), 0)
      : allAlerts.filter((a) => a.categoria === categoria).length;

  const contagem: Record<FiltroCategoria, number> = {
    todos: resumo ? resumo.total_geral : allAlerts.length,
    veiculos: contagemPorCategoria('veiculos'),
    motoristas: contagemPorCategoria('motoristas'),
    manutencoes: contagemPorCategoria('manutencoes'),
    despesas: contagemPorCategoria('despesas'),
  };

  // Listas truncadas dentro do filtro ativo: so elas justificam avisar o usuario
  // de que a tela nao esta mostrando tudo.
  const listasTruncadas: Array<{ slug: SlugAlerta; lista: ResumoDaLista }> = resumo
    ? SLUGS_POR_FILTRO[filtro]
        .map((slug) => ({ slug, lista: resumo.listas?.[slug] }))
        .filter((entrada): entrada is { slug: SlugAlerta; lista: ResumoDaLista } =>
          Boolean(entrada.lista?.truncado))
    : [];

  // "Tudo em dia!" e reservado a ausencia total de alertas na empresa; um filtro
  // vazio com alertas em outra categoria recebe mensagem propria.
  const semNenhumAlerta = resumo ? resumo.total_geral === 0 : allAlerts.length === 0;

  const rotuloDaLista = (slug: SlugAlerta) => resumo?.listas?.[slug]?.rotulo || ROTULO_PADRAO[slug];

  const abrirDetalhamento = (slug: SlugAlerta) => {
    const total = totalDaLista(slug);
    setItensDetalhe([]);
    // Semeia a paginacao com o total que o resumo ja conhece, para que o rodape
    // e os controles apareçam junto com a tela em vez de piscar depois da
    // resposta. A resposta do servidor sobrescreve esses numeros.
    setPaginacaoDetalhe({
      current_page: 1,
      last_page: Math.max(1, Math.ceil(total / ITENS_POR_PAGINA)),
      per_page: ITENS_POR_PAGINA,
      total,
    });
    setPaginaDetalhe(1);
    setListaDetalhada(slug);
  };

  // Volta ao painel preservando o filtro de categoria que o usuario ja tinha.
  const voltarAosAlertas = () => setListaDetalhada(null);

  const irParaPagina = (pagina: number) => setPaginaDetalhe(pagina);

  if (listaDetalhada) {
    const alertasDetalhe = itensDetalhe.map((item, i) => paraAlertaExibivel(item, listaDetalhada, i));

    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{rotuloDaLista(listaDetalhada)}</h1>
            <p className="text-gray-500 mt-1">Lista completa, página por página</p>
          </div>
          <button onClick={voltarAosAlertas}
            className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Voltar aos alertas
          </button>
        </div>

        <div className="space-y-3">
          {carregandoDetalhe && alertasDetalhe.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border">
              <p className="text-sm text-gray-500">Carregando alertas...</p>
            </div>
          ) : (
            alertasDetalhe.map((alerta) => <CartaoAlerta key={alerta.id} alerta={alerta} />)
          )}
        </div>

        {paginacaoDetalhe && (
          <div className="flex items-center justify-between px-4 py-3 border rounded-xl bg-gray-50">
            <span className="text-sm text-gray-600">
              {paginacaoDetalhe.total} alerta(s) · página {paginacaoDetalhe.current_page} de {paginacaoDetalhe.last_page}
            </span>
            <div className="flex gap-2">
              <button onClick={() => irParaPagina(paginacaoDetalhe.current_page - 1)}
                disabled={paginacaoDetalhe.current_page <= 1}
                className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40 hover:bg-white">Anterior</button>
              <button onClick={() => irParaPagina(paginacaoDetalhe.current_page + 1)}
                disabled={paginacaoDetalhe.current_page >= paginacaoDetalhe.last_page}
                className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-40 hover:bg-white">Próxima</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Alertas e vencimentos</h1>
          <p className="text-gray-500 mt-1">{contagem.todos} alerta(s) ativo(s)</p>
        </div>
        <button onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Atualizar
        </button>
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {CARDS_CATEGORIA.map((item) => {
          const Icon = item.icon;
          return (
            <button key={item.key} onClick={() => setFiltro(item.key)}
              className={`p-3 rounded-xl border text-left transition-all ${filtro === item.key ? 'ring-2 ring-blue-500 border-blue-300' : 'hover:shadow-sm'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-900">{contagem[item.key]}</p>
                  <p className="text-xs text-gray-500">{item.label}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Aviso de truncamento: a frase e montada sem espacos entre os nos para
          que o texto lido pelo usuario (e pelo leitor de tela) fique continuo. */}
      {listasTruncadas.length > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" aria-hidden="true" />
          <p className="text-sm text-amber-800">
            {'Nem todos os alertas cabem nesta lista: '}
            {listasTruncadas.map(({ slug, lista }, i) => (
              <React.Fragment key={slug}>
                {i > 0 ? ' · ' : ''}
                <button type="button"
                  onClick={() => abrirDetalhamento(slug)}
                  aria-label={`Ver todos os alertas de ${lista.rotulo}`}
                  className="underline font-medium hover:text-amber-900">
                  {`${lista.rotulo} (mostrando ${lista.exibidos} de ${lista.total})`}
                </button>
                {i === listasTruncadas.length - 1 ? '.' : ''}
              </React.Fragment>
            ))}
          </p>
        </div>
      )}

      {/* Lista de alertas */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <p className="text-sm text-gray-500">Carregando alertas...</p>
          </div>
        ) : alertasFiltrados.length > 0 ? (
          alertasFiltrados.map((alerta) => <CartaoAlerta key={alerta.id} alerta={alerta} />)
        ) : semNenhumAlerta ? (
          <div className="text-center py-16 bg-white rounded-xl border">
            <CheckCircle className="h-14 w-14 text-green-400 mx-auto mb-3" />
            <p className="text-lg font-medium text-gray-700">Tudo em dia!</p>
            <p className="text-sm text-gray-400 mt-1">Nenhum alerta pendente</p>
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-xl border">
            <CheckCircle className="h-14 w-14 text-green-400 mx-auto mb-3" />
            <p className="text-lg font-medium text-gray-700">Nenhum alerta nesta categoria</p>
            <p className="text-sm text-gray-400 mt-1">Há alertas em outras categorias</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertasList;
